/**
 * TreasuryChart - Graphique de trésorerie professionnel
 * Style QuickBooks/Sage avec time series
 */
import { 
  ComposedChart, 
  Bar, 
  Line, 
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend,
  ReferenceLine
} from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useCurrency } from '../../hooks/useCurrency';

export interface TreasuryDataPoint {
  date: string;
  totalCash: number;
  cashOnHand: number;
  bankBalance: number;
  inflow?: number;
  outflow?: number;
  netFlow?: number;
}

interface TreasuryChartProps {
  data: TreasuryDataPoint[];
  chartType?: 'bar' | 'area' | 'composed';
  showInflows?: boolean;
  showOutflows?: boolean;
  showNetPosition?: boolean;
  period?: 'day' | 'week' | 'month' | 'quarter';
  formatAmount?: (amount: number) => string;
  className?: string;
}

export function TreasuryChart({
  data,
  chartType = 'composed',
  showInflows = true,
  showOutflows = true,
  showNetPosition = true,
  period = 'month',
  formatAmount,
  className = ''
}: TreasuryChartProps) {
  const { format } = useCurrency();

  // Formatters
  const defaultFormatAmount = (amount: number) => {
    return format(amount);
  };

  const formatCompact = (value: number) => {
    if (Math.abs(value) >= 1000000000) {
      return `${(value / 1000000000).toFixed(1)}Md`;
    } else if (Math.abs(value) >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (Math.abs(value) >= 1000) {
      return `${(value / 1000).toFixed(0)}k`;
    }
    return value.toFixed(0);
  };

  const amountFormatter = formatAmount || defaultFormatAmount;

  // Format de date selon la période
  const formatDateLabel = (value: string) => {
    const date = new Date(value);
    switch (period) {
      case 'day':
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
      case 'week':
        return `S${Math.ceil(date.getDate() / 7)} ${date.toLocaleDateString('fr-FR', { month: 'short' })}`;
      case 'quarter':
        return `T${Math.ceil((date.getMonth() + 1) / 3)} ${date.getFullYear()}`;
      default:
        return date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
    }
  };

  // Calcul des tendances
  const getTrend = () => {
    if (data.length < 2) return { direction: 'stable', percentage: 0 };
    const first = data[0].totalCash;
    const last = data[data.length - 1].totalCash;
    const change = first > 0 ? ((last - first) / first) * 100 : 0;
    return {
      direction: change > 5 ? 'up' : change < -5 ? 'down' : 'stable',
      percentage: Math.abs(change)
    };
  };

  const trend = getTrend();

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const date = new Date(label);
    const formattedDate = date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    return (
      <div className="bg-card text-foreground border border-border rounded-xl shadow-lg p-4 min-w-[200px]">
        <p className="text-sm font-medium mb-3 border-b border-border/60 pb-2">
          {formattedDate}
        </p>
        <div className="space-y-2">
          {payload.map((entry: any, index: number) => {
            const labels: Record<string, string> = {
              totalCash: 'Trésorerie totale',
              cashOnHand: 'Caisse',
              bankBalance: 'Banque',
              inflow: 'Entrées',
              outflow: 'Sorties',
              netFlow: 'Flux net'
            };
            return (
              <div key={index} className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm text-muted-foreground">
                    {labels[entry.dataKey] || entry.dataKey}
                  </span>
                </div>
                <span className={`text-sm font-semibold ${
                  entry.dataKey === 'outflow' ? 'text-red-600' : 
                  entry.dataKey === 'inflow' ? 'text-green-600' : 
                  'text-foreground'
                }`}>
                  {amountFormatter(entry.value)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Header avec tendance */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Évolution de la trésorerie
          </h3>
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            trend.direction === 'up' 
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
              : trend.direction === 'down'
              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
          }`}>
            {trend.direction === 'up' && <TrendingUp className="h-3 w-3" />}
            {trend.direction === 'down' && <TrendingDown className="h-3 w-3" />}
            {trend.direction === 'stable' && <Minus className="h-3 w-3" />}
            {trend.percentage.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart 
          data={data} 
          margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
        >
          <defs>
            {/* Gradient pour la zone de trésorerie */}
            <linearGradient id="treasuryGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.02}/>
            </linearGradient>
            {/* Gradient pour les entrées */}
            <linearGradient id="inflowGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22c55e" stopOpacity={0.8}/>
              <stop offset="100%" stopColor="#22c55e" stopOpacity={0.4}/>
            </linearGradient>
            {/* Gradient pour les sorties */}
            <linearGradient id="outflowGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ef4444" stopOpacity={0.8}/>
              <stop offset="100%" stopColor="#ef4444" stopOpacity={0.4}/>
            </linearGradient>
          </defs>

          <CartesianGrid 
            strokeDasharray="3 3" 
            vertical={false} 
            stroke="currentColor"
            className="opacity-10"
          />

          <XAxis 
            dataKey="date" 
            tickFormatter={formatDateLabel}
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: 'currentColor', className: 'opacity-20' }}
          />

          <YAxis 
            tickFormatter={formatCompact}
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={50}
          />

          {/* Ligne de référence à zéro */}
          <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />

          <Tooltip content={<CustomTooltip />} />

          <Legend 
            wrapperStyle={{ fontSize: '12px', paddingTop: '16px' }}
            iconType="circle"
          />

          {/* Barres pour entrées/sorties */}
          {showInflows && (
            <Bar 
              dataKey="inflow" 
              fill="url(#inflowGradient)"
              radius={[4, 4, 0, 0]}
              name="Entrées"
              maxBarSize={30}
            />
          )}
          
          {showOutflows && (
            <Bar 
              dataKey="outflow" 
              fill="url(#outflowGradient)"
              radius={[4, 4, 0, 0]}
              name="Sorties"
              maxBarSize={30}
            />
          )}

          {/* Zone pour la position de trésorerie */}
          {showNetPosition && (
            <Area
              type="monotone"
              dataKey="totalCash"
              stroke="#3b82f6"
              strokeWidth={2}
              fill="url(#treasuryGradient)"
              name="Trésorerie totale"
              dot={false}
              activeDot={{ r: 4, fill: '#3b82f6' }}
            />
          )}

          {/* Ligne pour la tendance */}
          <Line
            type="monotone"
            dataKey="bankBalance"
            stroke="#8b5cf6"
            strokeWidth={2}
            dot={false}
            name="Banque"
            strokeDasharray="5 5"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
